import { useEffect, useRef } from "react";
import { MultiSelectOptions } from "../../../../internal/components/MultiSelectOptions";
import { OptionsPanelHandle } from "../../../../internal/components/OptionsPanel";
import { MultiSelectFieldInput } from "./MultiSelectFieldInput";
import { SelectFieldLabel } from "../../SelectField/internal/SelectFieldLabel";
import { useDebouncedCallback } from "../../../../internal/hooks/useDebouncedCallback";
import styles from "../../../../components/SelectTrigger/internal/SelectTriggerBase.module.scss";
import { useComboMultiple } from "./useComboMultiple";
import { OptionsPopover } from "../../../../internal/components/OptionsPopover/OptionsPopover";
import { OptionsDialog } from "../../../../internal/components/OptionsDialog/OptionsDialog";
import { FieldMessage } from "../../../../components/FieldMessage";
import { MultiSelectFieldComboboxInternalProps } from "./types";

/**
 * Combobox mode: searchable multi-select using useCombobox.
 */
export const MultiSelectFieldComboboxMode = ({
  labelAiMark,
  id,
  helperUid,
  selectedOptions,
  onSelectedOptionsChange,
  displayAs,
  isDisabledOrReadOnly,
  disabled,
  readOnly,
  required,
  placeholder,
  size,
  error,
  hasHelperText,
  disableClearButton,
  prefix,
  suffix,
  label,
  hideLabel,
  hint,
  errorMessages,
  warning,
  description,
  className,
  style,
  layoutStyles,
  pinned,
  groupToString,
  groupSorter,
  virtualize,
  options,
  loading,
  loadingMore,
  hasMore,
  loadOptions,
  loadMore,
  initialLoad,
  initialLoadPerformed,
  setInitialLoadPerformed,
  inputWrapperRef,
  debounceMs,
  selectAll,
  selectFiltered,
  singleRow,
  maxChips,
  getChipProps,
  onSearchChange,
  handleRemoveOption,
  handleClear,
}: MultiSelectFieldComboboxInternalProps) => {
  const listRef = useRef<OptionsPanelHandle>(null);

  const debouncedOptionLoader = useDebouncedCallback((inputValue: string) => {
    loadOptions(inputValue, { initial: true });
  }, debounceMs);

  const {
    isOpen,
    getToggleButtonProps,
    getLabelProps,
    getMenuProps,
    getInputProps,
    wrapperDivRef,
    highlightedIndex,
    getItemProps,
    inputValue,
    items,
    sectionsMeta,
    closeMenu,
    toggleMenu,
  } = useComboMultiple({
    displayAs,
    onSelectedOptionsChange,
    disabled: isDisabledOrReadOnly,
    options,
    loading,
    pinned,
    virtualize,
    groupToString,
    groupSorter,
    selectedOptions,
    selectAll,
    selectFiltered,
    async onIsOpenChange(changes) {
      if (initialLoad === "open" && !initialLoadPerformed && changes.isOpen) {
        await loadOptions("", { initial: true });
        setInitialLoadPerformed(true);
      }
    },
    onInputValueChange: ({ inputValue: iv }) => {
      debouncedOptionLoader(iv ?? "");
      onSearchChange?.(iv ?? "");
    },
    onScrollToHighlight: (i) => listRef.current?.scrollToIndex(i),
  });

  useEffect(() => {
    const performInitialLoad = async () => {
      if (initialLoad === "immediate" && !initialLoadPerformed) {
        await loadOptions("", { initial: true });
        setInitialLoadPerformed(true);
      }
    };
    performInitialLoad();
  }, [loadOptions, initialLoadPerformed, initialLoad, setInitialLoadPerformed]);

  const handleLoadMore = async () => {
    await loadMore(inputValue);
  };

  if (displayAs === "dialog") {
    return (
      <div
        className={
          className
            ? `${styles["search-field"]} ${className}`
            : styles["search-field"]
        }
        style={{ ...layoutStyles, ...style }}
        ref={wrapperDivRef}
      >
        <SelectFieldLabel
          aiMark={labelAiMark}
          label={label}
          hideLabel={hideLabel}
          getLabelProps={getLabelProps}
          required={required}
        />
        <MultiSelectFieldInput
          disabled={disabled}
          readOnly={readOnly}
          disableToggleButton={false}
          placeholder={placeholder}
          disableClearButton={disableClearButton}
          toggleButtonProps={getToggleButtonProps()}
          onClear={handleClear}
          id={`${id}-input`}
          inputProps={getInputProps({
            "aria-describedby": hasHelperText ? helperUid : undefined,
            "aria-invalid": error ? true : undefined,
          })}
          size={size}
          error={!!error}
          prefix={prefix}
          suffix={suffix}
          selectedOptions={selectedOptions}
          onRemoveOption={handleRemoveOption}
          singleRow={singleRow}
          maxChips={maxChips}
          getChipProps={getChipProps}
          onWrapperClick={toggleMenu}
        />
        {hasHelperText ? (
          <FieldMessage
            id={helperUid}
            hint={hint}
            error={errorMessages}
            warning={warning}
            description={description}
          />
        ) : null}
        <OptionsDialog
          id={`${id}-dialog`}
          isOpen={isOpen}
          onClose={closeMenu}
          title={label}
          initialFocusResolver={(elements) => {
            return (
              elements.find((element) => element.tagName === "INPUT") ||
              elements[0]
            );
          }}
          field={
            <MultiSelectFieldInput
              disabled={disabled}
              readOnly={readOnly}
              inputProps={getInputProps()}
              disableToggleButton
              placeholder={placeholder}
              disableClearButton={disableClearButton}
              onClear={handleClear}
              id={`${id}-dialog-input`}
              inputWrapperRef={inputWrapperRef}
              size="medium"
              error={!!error}
              prefix={prefix}
              suffix={suffix}
              selectedOptions={selectedOptions}
              onRemoveOption={handleRemoveOption}
              singleRow
              maxChips={20}
              getChipProps={getChipProps}
            />
          }
        >
          <MultiSelectOptions
            isOpen
            items={items}
            getMenuProps={getMenuProps}
            ref={listRef}
            getItemProps={getItemProps}
            highlightedIndex={highlightedIndex}
            selectedOptions={selectedOptions}
            hasMore={hasMore}
            onLoadMore={handleLoadMore}
            loading={loading}
            loadingMore={loadingMore}
            disabled={isDisabledOrReadOnly}
            sectionsMeta={sectionsMeta}
            virtualize={virtualize}
          />
        </OptionsDialog>
      </div>
    );
  }

  return (
    <div
      className={
        className
          ? `${styles["search-field"]} ${className}`
          : styles["search-field"]
      }
      style={{ ...layoutStyles, ...style }}
    >
      <SelectFieldLabel
        aiMark={labelAiMark}
        label={label}
        hideLabel={hideLabel}
        getLabelProps={getLabelProps}
        required={required}
      />
      <MultiSelectFieldInput
        disabled={disabled}
        readOnly={readOnly}
        inputProps={getInputProps({
          popovertarget: `${id}-popover`,
          "aria-describedby": hasHelperText ? helperUid : undefined,
          "aria-invalid": error ? true : undefined,
        })}
        toggleButtonProps={getToggleButtonProps()}
        disableToggleButton={false}
        placeholder={placeholder}
        disableClearButton={disableClearButton}
        onClear={handleClear}
        id={`${id}-input`}
        inputWrapperRef={inputWrapperRef}
        size={size}
        error={!!error}
        prefix={prefix}
        suffix={suffix}
        selectedOptions={selectedOptions}
        onRemoveOption={handleRemoveOption}
        singleRow={singleRow}
        maxChips={maxChips}
        getChipProps={getChipProps}
        onWrapperClick={toggleMenu}
      />
      {hasHelperText ? (
        <FieldMessage
          id={helperUid}
          hint={hint}
          error={errorMessages}
          warning={warning}
          description={description}
        />
      ) : null}
      <OptionsPopover
        id={`${id}-popover`}
        referenceElement={inputWrapperRef}
        open={isOpen && displayAs === "popover"}
        onClickOutside={closeMenu}
        width="reference"
      >
        <MultiSelectOptions
          isOpen={isOpen}
          items={items}
          getMenuProps={getMenuProps}
          ref={listRef}
          getItemProps={getItemProps}
          highlightedIndex={highlightedIndex}
          selectedOptions={selectedOptions}
          hasMore={hasMore}
          onLoadMore={handleLoadMore}
          loading={loading}
          loadingMore={loadingMore}
          disabled={isDisabledOrReadOnly}
          sectionsMeta={sectionsMeta}
          virtualize={virtualize}
        />
      </OptionsPopover>
    </div>
  );
};
